import * as React from "react";
import { AlertColor, MenuItem, Stack, TextField } from "@mui/material";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import {
    useCreatePayrollEventMutation,
    useGetClientListQuery,
    useGetGlDataQuery,
    useGetReferenceDataQuery,
} from "../../../services/cclTokenedPayrollApi";

export type ProjectFormData = {
    projectId: string;
    sessionTitle: string;
    sessionType: string;
    customer: string;
    adminLoc: string;
    glGroup: string;
};

interface AddProjectDrawerProps {
    open: boolean;
    onClose: () => void;
    onSaveResult: (message: string, severity: AlertColor) => void;
}

const initialFormData: ProjectFormData = {
    projectId: "",
    sessionTitle: "",
    sessionType: "",
    customer: "",
    adminLoc: "",
    glGroup: "",
};

const alphabetical = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
const AddProjectDrawer: React.FC<AddProjectDrawerProps> = (props) => {
    const [formData, setFormData] = React.useState<ProjectFormData>(initialFormData);
    const [createPayrollEvent, { isLoading: isSaving }] = useCreatePayrollEventMutation();
    const { logEvent } = useLogAccessEvent();
    const {
        data: referenceData,
        isLoading: isReferenceDataLoading,
        isError: isReferenceDataError,
    } = useGetReferenceDataQuery();
    const {
        data: glData,
        isLoading: isGlDataLoading,
        isError: isGlDataError,
    } = useGetGlDataQuery();
    const {
        data: clientData,
        isLoading: isClientDataLoading,
        isError: isClientDataError,
    } = useGetClientListQuery();

    const sessionTypes = (referenceData?.linesOfBusiness ?? [])
        .filter((lob: any) => lob.active !== false && String(lob.active).toLowerCase() !== "false")
        .sort((a: any, b: any) => {
            const text = (lob: any) => String(lob.lobDescription ?? lob.description ?? lob.lobCode ?? lob.lobId ?? lob.id ?? "");
            return alphabetical.compare(text(a), text(b));
        });
    const adminLocations = (referenceData?.locations ?? [])
        .filter((location: any) => location.active !== false && String(location.active).toLowerCase() !== "false")
        .sort((a: any, b: any) => {
            const text = (location: any) => String(location.locDesc ?? location.locCode ?? "");
            return alphabetical.compare(text(a), text(b));
        });
    const glGroups = (glData?.glGroups ?? [])
        .filter((group: any) => group.active !== false && String(group.active).toLowerCase() !== "false")
        .sort((a: any, b: any) => {
            const text = (group: any) => String(group.groupDesc ?? group.groupCode ?? "");
            return alphabetical.compare(text(a), text(b));
        });
    const clients = (Array.isArray(clientData) ? clientData : clientData?.data ?? [])
        .map((client: any) => String(client.clientName ?? "").trim())
        .filter((clientName: string) => clientName !== "")
        .sort(alphabetical.compare);

    React.useEffect(() => {
        if (props.open) {
            setFormData(initialFormData);
        }
    }, [props.open]);

    const handleFieldChange = (field: keyof ProjectFormData, value: string) => {
        setFormData((current) => ({ ...current, [field]: value }));
    };

    const canSave = Object.values(formData).every((value) => value.trim() !== "");

    const handleSave = async () => {
        if (!canSave || isSaving) {
            return;
        }

        try {
            await createPayrollEvent({
                esKey: null,
                eventCode: formData.projectId,
                projectName: formData.sessionTitle,
                lob: formData.sessionType,
                adminLoc: formData.adminLoc,
                glGroup: formData.glGroup,
                client: formData.customer,
            }).unwrap();
            logEvent("PayrollProjectCreated", {
                sessionID: "",
                projectCode: formData.projectId,
            });
            props.onSaveResult("Payroll project created", "success");
            props.onClose();
        } catch (error) {
            console.error("Failed to create payroll project:", error);
            props.onSaveResult("Failed to create project. Please try again.", "error");
        }
    };

    return (
        <CclDrawer title="Create Project" open={props.open} onClose={props.onClose}>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                    id="create-project-id"
                    label="Project ID"
                    size="small"
                    fullWidth
                    required
                    value={formData.projectId}
                    onChange={(event) => handleFieldChange("projectId", event.target.value)}
                />
                <TextField
                    id="create-project-name"
                    label="Session Title"
                    size="small"
                    fullWidth
                    required
                    value={formData.sessionTitle}
                    onChange={(event) => handleFieldChange("sessionTitle", event.target.value)}
                />
                <TextField
                    id="create-project-customer"
                    label="Customer"
                    size="small"
                    fullWidth
                    select
                    value={formData.customer}
                    onChange={(event) => handleFieldChange("customer", event.target.value)}
                >
                    <MenuItem value="" disabled>
                        {isClientDataLoading
                            ? "Loading customers..."
                            : isClientDataError
                                ? "Unable to load customers"
                                : clients.length === 0
                                    ? "No customers available"
                                    : "Select customer"}
                    </MenuItem>
                    {clients.map((clientName: string) => (
                        <MenuItem key={clientName} value={clientName}>
                            {clientName}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    id="create-project-session-type"
                    label="Session Type"
                    size="small"
                    fullWidth
                    required
                    select
                    value={formData.sessionType}
                    onChange={(event) => handleFieldChange("sessionType", event.target.value)}
                >
                    <MenuItem value="" disabled>
                        {isReferenceDataLoading
                            ? "Loading session types..."
                            : isReferenceDataError
                                ? "Unable to load session types"
                                : "Select session type"}
                    </MenuItem>
                    {sessionTypes.map((sessionType: any) => {
                        const code = String(sessionType.lobCode);
                        const description = String(sessionType.lobDescription);
                        return code ? (
                            <MenuItem key={code} value={code}>
                                {description ? `${code} - ${description}` : code}
                            </MenuItem>
                        ) : null;
                    })}
                </TextField>
                <TextField
                    id="create-project-admin-location"
                    label="Admin. Location"
                    size="small"
                    fullWidth
                    required
                    select
                    value={formData.adminLoc}
                    onChange={(event) => handleFieldChange("adminLoc", event.target.value)}
                >
                    <MenuItem value="" disabled>
                        {isReferenceDataLoading
                            ? "Loading admin. locations..."
                            : isReferenceDataError
                                ? "Unable to load admin. locations"
                                : "Select admin. location"}
                    </MenuItem>
                    {adminLocations.map((location: any) => {
                        const code = String(location.locCode ?? "");
                        const description = String(location.locDesc ?? "");
                        return code ? (
                            <MenuItem key={code} value={code}>
                                {description ? `${code} - ${description}` : code}
                            </MenuItem>
                        ) : null;
                    })}
                </TextField>
                <TextField
                    id="create-project-gl-group"
                    label="GL Group"
                    size="small"
                    fullWidth
                    required
                    select
                    value={formData.glGroup}
                    onChange={(event) => handleFieldChange("glGroup", event.target.value)}
                >
                    <MenuItem value="" disabled>
                        {isGlDataLoading
                            ? "Loading GL groups..."
                            : isGlDataError
                                ? "Unable to load GL groups"
                                : "Select GL group"}
                    </MenuItem>
                    {glGroups.map((group: any) => {
                        const code = String(group.groupCode ?? "");
                        const description = String(group.groupDesc ?? "");
                        return code ? (
                            <MenuItem key={code} value={code}>
                                {description ? `${code} - ${description}` : code}
                            </MenuItem>
                        ) : null;
                    })}
                </TextField>
                <CclDrawerActionBar>
                    <CclLoadingButton
                        mode="primary"
                        loading={isSaving}
                        disabled={!canSave || isSaving}
                        onClick={handleSave}
                    >
                        Save
                    </CclLoadingButton>
                    <CclButton mode="secondary" onClick={props.onClose}>
                        Cancel
                    </CclButton>
                </CclDrawerActionBar>
            </Stack>
        </CclDrawer>
    );
};

export default AddProjectDrawer;
